import { useState } from 'react';
import { Link } from 'react-router-dom';
import AdSlot from '../components/Ads/AdSlot';
import { metroCities, homeLanguages, type MetroCityCatalogEntry } from '../cities/catalog';
import type { MetroLanguage } from '../types/metro';

type HomeCopy={
  eyebrow:string;
  title:string;
  intro:string;
  explore:string;
  metro:string;
  open:(city:string)=>string;
  languageLabel:string;
  aboutTitle:string;
  aboutText:string;
  howTitle:string;
  howText:string;
  privacyTitle:string;
  privacyText:string;
};
const HOME_COPY:Record<string,HomeCopy>={
  en:{
    eyebrow:'PUBLIC TRANSPORT INTERACTIVE MAP',
    title:'Metro maps that help you understand the journey.',
    intro:'Explore public transport systems city by city. Choose a city below to open its interactive map.',
    explore:'Explore cities',
    metro:'Metro',
    open:city=>`Open ${city} Metro map`,
    languageLabel:'Language',
    aboutTitle:'What Vadodo is about',
    aboutText:'Vadodo is an interactive public transport map designed to make metro networks easier to understand. Instead of only showing a route as text, Vadodo helps you see the lines, stations, transfers and the journey directly on the map.',
    howTitle:'How to use it',
    howText:'Choose a city, then select your starting station and destination either from the map or by searching for the station names. Vadodo calculates the route and presents the journey visually, including transfers and available route alternatives when applicable.',
    privacyTitle:'Privacy and data',
    privacyText:'Vadodo does not require an account and does not collect or store your personal information, route searches or journey history on Vadodo servers. Where the site remembers preferences or recent activity, that information is stored locally in your browser. Third-party services such as advertising providers may operate under their own privacy policies when enabled.'
  },
  ro:{
    eyebrow:'HARTĂ INTERACTIVĂ DE TRANSPORT PUBLIC',
    title:'Hărți de metrou care te ajută să înțelegi călătoria.',
    intro:'Explorează transportul public oraș cu oraș. Alege un oraș pentru a deschide harta interactivă.',
    explore:'Explorează orașele',
    metro:'Metrou',
    open:city=>`Deschide harta metroului din ${city}`,
    languageLabel:'Limbă',
    aboutTitle:'Ce este Vadodo',
    aboutText:'Vadodo este o hartă interactivă de transport public creată pentru a face rețelele de metrou mai ușor de înțeles. În loc să afișeze doar traseul ca text, Vadodo te ajută să vezi liniile, stațiile, schimbările și călătoria direct pe hartă.',
    howTitle:'Cum se folosește',
    howText:'Alege un oraș, apoi selectează stația de plecare și destinația direct de pe hartă sau căutând numele stațiilor. Vadodo calculează traseul și prezintă vizual călătoria, inclusiv schimbările și rutele alternative disponibile, atunci când este cazul.',
    privacyTitle:'Confidențialitate și date',
    privacyText:'Vadodo nu necesită cont și nu colectează sau stochează informațiile tale personale, căutările de trasee sau istoricul călătoriilor pe serverele Vadodo. Atunci când site-ul reține preferințe sau activitate recentă, aceste informații sunt păstrate local în browserul tău. Serviciile terțe, cum ar fi furnizorii de publicitate, pot funcționa conform propriilor politici de confidențialitate atunci când sunt activate.'
  },
  fr:{
    eyebrow:'CARTE INTERACTIVE DES TRANSPORTS PUBLICS',
    title:'Des plans de métro qui vous aident à comprendre votre trajet.',
    intro:'Explorez les réseaux de transports publics ville par ville. Choisissez une ville ci-dessous pour ouvrir sa carte interactive.',
    explore:'Explorer les villes',
    metro:'Métro',
    open:city=>`Ouvrir le plan du métro de ${city}`,
    languageLabel:'Langue',
    aboutTitle:'À propos de Vadodo',
    aboutText:'Vadodo est une carte interactive des transports publics conçue pour rendre les réseaux de métro plus faciles à comprendre. Au lieu de présenter uniquement un itinéraire sous forme de texte, Vadodo vous permet de voir directement sur la carte les lignes, les stations, les correspondances et le trajet.',
    howTitle:'Comment l’utiliser',
    howText:'Choisissez une ville, puis sélectionnez votre station de départ et votre destination sur la carte ou en recherchant le nom des stations. Vadodo calcule l’itinéraire et présente le trajet visuellement, avec les correspondances et, lorsqu’elles existent, les différentes options de trajet.',
    privacyTitle:'Confidentialité et données',
    privacyText:'Vadodo ne nécessite aucun compte et ne collecte ni ne stocke vos informations personnelles, vos recherches d’itinéraires ou votre historique de trajets sur les serveurs Vadodo. Lorsque le site mémorise des préférences ou une activité récente, ces informations restent stockées localement dans votre navigateur. Les services tiers, tels que les fournisseurs de publicité, peuvent appliquer leurs propres politiques de confidentialité lorsqu’ils sont activés.'
  },
  da:{
    eyebrow:'INTERAKTIVT KORT OVER OFFENTLIG TRANSPORT',
    title:'Metrokort, der gør din rejse nemmere at forstå.',
    intro:'Udforsk offentlige transportsystemer by for by. Vælg en by nedenfor for at åbne dens interaktive kort.',
    explore:'Udforsk byer',
    metro:'Metro',
    open:city=>`Åbn metrokortet for ${city}`,
    languageLabel:'Sprog',
    aboutTitle:'Hvad Vadodo handler om',
    aboutText:'Vadodo er et interaktivt kort over offentlig transport, der gør metronet lettere at forstå. I stedet for kun at vise en rute som tekst hjælper Vadodo dig med at se linjer, stationer, skift og selve rejsen direkte på kortet.',
    howTitle:'Sådan bruger du det',
    howText:'Vælg en by, og vælg derefter din startstation og destination enten direkte på kortet eller ved at søge efter stationsnavnene. Vadodo beregner ruten og viser rejsen visuelt, inklusive skift og alternative ruter, når de findes.',
    privacyTitle:'Privatliv og data',
    privacyText:'Vadodo kræver ikke en konto og indsamler eller gemmer ikke dine personlige oplysninger, rutesøgninger eller rejsehistorik på Vadodos servere. Når siden husker indstillinger eller nylig aktivitet, gemmes disse oplysninger lokalt i din browser. Tredjepartstjenester som annonceudbydere kan følge deres egne privatlivspolitikker, når de er aktiveret.'
  },
  sv:{
    eyebrow:'INTERAKTIV KARTA FÖR KOLLEKTIVTRAFIK',
    title:'Metrokartor som hjälper dig att förstå resan.',
    intro:'Utforska kollektivtrafiksystem stad för stad. Välj en stad nedan för att öppna dess interaktiva karta.',
    explore:'Utforska städer',
    metro:'Metro',
    open:city=>`Öppna metrokartan för ${city}`,
    languageLabel:'Språk',
    aboutTitle:'Vad Vadodo handlar om',
    aboutText:'Vadodo är en interaktiv kollektivtrafikkarta som gör metronät enklare att förstå. I stället för att bara visa en resväg som text hjälper Vadodo dig att se linjer, stationer, byten och själva resan direkt på kartan.',
    howTitle:'Så använder du tjänsten',
    howText:'Välj en stad och välj sedan startstation och destination antingen direkt på kartan eller genom att söka efter stationernas namn. Vadodo beräknar resvägen och visar resan visuellt, inklusive byten och alternativa resvägar när sådana finns.',
    privacyTitle:'Integritet och data',
    privacyText:'Vadodo kräver inget konto och samlar inte in eller lagrar dina personuppgifter, ruttsökningar eller resehistorik på Vadodos servrar. När webbplatsen kommer ihåg inställningar eller senaste aktivitet lagras informationen lokalt i din webbläsare. Tredjepartstjänster, till exempel annonsleverantörer, kan använda sina egna integritetspolicyer när de är aktiverade.'
  },
  cs:{
    eyebrow:'INTERAKTIVNÍ MAPA VEŘEJNÉ DOPRAVY',
    title:'Mapy metra, které vám pomohou pochopit cestu.',
    intro:'Prozkoumejte systémy veřejné dopravy město po městě. Níže vyberte město a otevřete jeho interaktivní mapu.',
    explore:'Prozkoumat města',
    metro:'Metro',
    open:city=>`Otevřít mapu metra ${city}`,
    languageLabel:'Jazyk',
    aboutTitle:'O čem je Vadodo',
    aboutText:'Vadodo je interaktivní mapa veřejné dopravy navržená tak, aby byly sítě metra snadněji pochopitelné. Místo pouhého textového popisu trasy vám Vadodo umožní vidět linky, stanice, přestupy a celou cestu přímo na mapě.',
    howTitle:'Jak ji používat',
    howText:'Vyberte město a poté zvolte výchozí stanici a cíl přímo na mapě nebo vyhledáním názvů stanic. Vadodo vypočítá trasu a zobrazí cestu vizuálně, včetně přestupů a dostupných alternativních tras, pokud existují.',
    privacyTitle:'Soukromí a data',
    privacyText:'Vadodo nevyžaduje účet a na svých serverech neshromažďuje ani neukládá vaše osobní údaje, vyhledané trasy ani historii cest. Pokud si web pamatuje nastavení nebo nedávnou aktivitu, ukládají se tyto informace pouze lokálně ve vašem prohlížeči. Služby třetích stran, například poskytovatelé reklamy, se po aktivaci mohou řídit vlastními zásadami ochrany soukromí.'
  },
  pl:{
    eyebrow:'INTERAKTYWNA MAPA TRANSPORTU PUBLICZNEGO',
    title:'Mapy metra, które pomagają zrozumieć podróż.',
    intro:'Poznawaj systemy transportu publicznego miasto po mieście. Wybierz miasto poniżej, aby otworzyć jego interaktywną mapę.',
    explore:'Przeglądaj miasta',
    metro:'Metro',
    open:city=>`Otwórz mapę metra ${city}`,
    languageLabel:'Język',
    aboutTitle:'Czym jest Vadodo',
    aboutText:'Vadodo to interaktywna mapa transportu publicznego zaprojektowana tak, aby sieci metra były łatwiejsze do zrozumienia. Zamiast pokazywać trasę wyłącznie jako tekst, Vadodo pozwala zobaczyć linie, stacje, przesiadki i całą podróż bezpośrednio na mapie.',
    howTitle:'Jak korzystać',
    howText:'Wybierz miasto, a następnie stację początkową i miejsce docelowe bezpośrednio na mapie albo wyszukując nazwy stacji. Vadodo obliczy trasę i przedstawi podróż wizualnie, wraz z przesiadkami i dostępnymi trasami alternatywnymi, jeśli występują.',
    privacyTitle:'Prywatność i dane',
    privacyText:'Vadodo nie wymaga konta i nie gromadzi ani nie przechowuje na swoich serwerach danych osobowych, wyszukiwanych tras ani historii podróży. Jeśli strona zapamiętuje ustawienia lub ostatnią aktywność, informacje te są przechowywane lokalnie w przeglądarce. Usługi zewnętrzne, takie jak dostawcy reklam, po włączeniu mogą działać zgodnie z własnymi politykami prywatności.'
  },
  hu:{
    eyebrow:'INTERAKTÍV TÖMEGKÖZLEKEDÉSI TÉRKÉP',
    title:'Metrótérképek, amelyek segítenek megérteni az utazást.',
    intro:'Fedezd fel a tömegközlekedési rendszereket városról városra. Válassz egy várost az interaktív térkép megnyitásához.',
    explore:'Városok felfedezése',
    metro:'Metró',
    open:city=>`${city} metrótérképének megnyitása`,
    languageLabel:'Nyelv',
    aboutTitle:'Miről szól a Vadodo',
    aboutText:'A Vadodo egy interaktív tömegközlekedési térkép, amely könnyebben érthetővé teszi a metróhálózatokat. Ahelyett, hogy csak szövegként mutatná az útvonalat, a Vadodo közvetlenül a térképen jeleníti meg a vonalakat, állomásokat, átszállásokat és az egész utazást.',
    howTitle:'Hogyan használd',
    howText:'Válassz egy várost, majd add meg az induló állomást és a célállomást közvetlenül a térképen vagy az állomásnevek keresésével. A Vadodo kiszámítja az útvonalat, és vizuálisan mutatja be az utazást, beleértve az átszállásokat és az elérhető alternatív útvonalakat.',
    privacyTitle:'Adatvédelem és adatok',
    privacyText:'A Vadodo használatához nincs szükség fiókra, és a Vadodo szerverein nem gyűjtjük vagy tároljuk személyes adataidat, útvonalkereséseidet vagy utazási előzményeidet. Ha az oldal megjegyez beállításokat vagy legutóbbi tevékenységet, ezek az adatok helyben, a böngésződben tárolódnak. A harmadik fél szolgáltatásai, például a hirdetési szolgáltatók, aktiválásuk esetén a saját adatvédelmi szabályaik szerint működhetnek.'
  },
  bg:{
    eyebrow:'ИНТЕРАКТИВНА КАРТА НА ГРАДСКИЯ ТРАНСПОРТ',
    title:'Метро карти, които ви помагат да разберете пътуването.',
    intro:'Разглеждайте системите за градски транспорт град по град. Изберете град по-долу, за да отворите интерактивната му карта.',
    explore:'Разгледайте градовете',
    metro:'Метро',
    open:city=>`Отвори картата на метрото в ${city}`,
    languageLabel:'Език',
    aboutTitle:'Какво представлява Vadodo',
    aboutText:'Vadodo е интерактивна карта на градския транспорт, създадена да направи метро мрежите по-лесни за разбиране. Вместо да показва маршрута само като текст, Vadodo ви помага да видите линиите, станциите, прекачванията и самото пътуване директно върху картата.',
    howTitle:'Как се използва',
    howText:'Изберете град, след което задайте начална станция и дестинация директно от картата или чрез търсене по име на станцията. Vadodo изчислява маршрута и показва пътуването визуално, включително прекачванията и наличните алтернативни маршрути, когато има такива.',
    privacyTitle:'Поверителност и данни',
    privacyText:'Vadodo не изисква акаунт и не събира или съхранява вашите лични данни, търсения на маршрути или история на пътуванията на сървърите на Vadodo. Когато сайтът запомня настройки или скорошна активност, тази информация се съхранява локално във вашия браузър. Услуги на трети страни, като доставчици на реклами, могат да работят според собствените си политики за поверителност, когато са активирани.'
  }
};

const preferredHomeLanguage=():MetroLanguage=>{
  const stored=localStorage.getItem('hopply.language')||'en';
  return HOME_COPY[stored]?stored:'en';
};
const cityNameFor=(city:MetroCityCatalogEntry,language:MetroLanguage)=>{
  if(language==='en'&&city.localName&&city.localName!==city.name)return `${city.name} (${city.localName})`;
  return city.localizedNames?.[language]??city.localName??city.name;
};

export default function HomePage() {
  const [language,setLanguageState]=useState<MetroLanguage>(preferredHomeLanguage);
  const t=HOME_COPY[language]??HOME_COPY.en;
  const setLanguage=(nextLanguage:MetroLanguage)=>{
    setLanguageState(nextLanguage);
    localStorage.setItem('hopply.language',nextLanguage);
  };

  return (
    <main className="landing">
      <section>
        <div className="landingHeader">
          <div className="siteBrand" aria-label="Vadodo">
            <img src="/branding/vadodo-mark.svg" alt="" aria-hidden="true" />
            <span>Vadodo</span>
          </div>
          <label className="homeLanguageSelector">
            <span>{t.languageLabel}</span>
            <select value={language} onChange={event=>setLanguage(event.target.value)} aria-label={t.languageLabel}>
              {homeLanguages.map(option=><option key={option.code} value={option.code}>{option.nativeName}</option>)}
            </select>
          </label>
        </div>
        <p className="eyebrow">{t.eyebrow}</p>
        <h1>{t.title}</h1>
        <p>{t.intro}</p>

        <AdSlot className="landingPrimaryAd" slot="7127657846" placeholder="Google Ads Here Soon" />

        <div className="citySection">
          <h2>{t.explore}</h2>
          <div className="cityGrid">
            {metroCities.map(city=>{
              const displayName=cityNameFor(city,language);
              const country=city.localizedCountries?.[language]??city.country;
              return <Link key={city.id} className="cityCard" to={`/${city.id}`} aria-label={t.open(displayName)} data-google-vignette="false">
                <div>
                  <span className="cityName">{displayName}</span>
                  <span className="citySystem">{t.metro}</span>
                  <span className="cityCountry">{country}</span>
                </div>
                <span className="cityArrow" aria-hidden="true">→</span>
              </Link>;
            })}
          </div>
        </div>

        <AdSlot className="landingBottomAd" slot="2988750633" placeholder="Google Ads Here Soon" />

        <section className="landingInfo" aria-label={t.aboutTitle}>
          <div>
            <h2>{t.aboutTitle}</h2>
            <p>{t.aboutText}</p>
          </div>
          <div>
            <h2>{t.howTitle}</h2>
            <p>{t.howText}</p>
          </div>
          <div>
            <h2>{t.privacyTitle}</h2>
            <p>{t.privacyText}</p>
          </div>
        </section>
      </section>
    </main>
  );
}
